import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './ReservarTurno.css';

const HORA_INICIO = 10;
const HORA_FIN = 20;

const ReservarTurno = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Estados de datos
    const [masajista, setMasajista] = useState(null);
    const [servicios, setServicios] = useState([]);
    const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
    const [fecha, setFecha] = useState('');
    const [horariosDisponibles, setHorariosDisponibles] = useState([]);
    const [turnosOcupados, setTurnosOcupados] = useState([]);
    
    // Estados de bloqueos
    const [diasBloqueados, setDiasBloqueados] = useState([]);
    const [horasBloqueadas, setHorasBloqueadas] = useState({});
    const [diaEstaBloqueado, setDiaEstaBloqueado] = useState(false);

    // Estados del formulario
    const [notas, setNotas] = useState('');
    const [dosTurnos, setDosTurnos] = useState(false);
    const [hora1, setHora1] = useState('');
    const [hora2, setHora2] = useState('');

    // Estados de UI
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exito, setExito] = useState('');
    const [procesandoPago, setProcesandoPago] = useState(false);
    
    // Estados del Modal de Pago
    const [showModalPago, setShowModalPago] = useState(false);
    const [metodoPago, setMetodoPago] = useState(''); // 'mercadopago' | 'transferencia'
    const [comprobante, setComprobante] = useState(null);
    const [idsTurnosCreados, setIdsTurnosCreados] = useState([]);

    const hoy = new Date();
    const fechaMin = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    const fechaMaxDate = new Date(hoy);
    fechaMaxDate.setDate(fechaMaxDate.getDate() + 30);
    const fechaMax = `${fechaMaxDate.getFullYear()}-${String(fechaMaxDate.getMonth() + 1).padStart(2, '0')}-${String(fechaMaxDate.getDate()).padStart(2, '0')}`;

    // CARGA INICIAL
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [resTurnos, resList, resServicios] = await Promise.all([
                    api.get(`/appointments/masajista/${id}/turnos`),
                    api.get('/appointments/masajistas'),
                    api.get('/servicios/activos'),
                ]);

                setTurnosOcupados(resTurnos.data.map(t => new Date(t.fecha).getTime()));

                const encontrado = resList.data.find(m => m._id === id);
                if (encontrado) setMasajista(encontrado);
                else setError('Masajista no encontrado');

                setServicios(resServicios.data);
                if (resServicios.data.length > 0) {
                    setServicioSeleccionado(resServicios.data[0]);
                }

                try {
                    const resBloqueos = await api.get(`/agenda/masajista/${id}/bloqueados`);
                    setDiasBloqueados(resBloqueos.data.diasCompletos || []);
                    setHorasBloqueadas(resBloqueos.data.horasParciales || {});
                } catch (blockErr) {
                    console.warn('No se pudieron cargar los bloqueos:', blockErr);
                }

            } catch (err) {
                setError('Error al cargar datos');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    // VALIDAR AL SELECCIONAR EL DÍA
    const handleFechaChange = (e) => {
        const nuevaFecha = e.target.value;
        
        if (diasBloqueados.includes(nuevaFecha)) {
            setDiaEstaBloqueado(true);
            setFecha(nuevaFecha);
            setHora1('');
            setHora2('');
            setDosTurnos(false);
            return;
        }
        
        setDiaEstaBloqueado(false);
        setError('');
        setFecha(nuevaFecha);
    };

    // CALCULAR HORARIOS DISPONIBLES (RESTANDO OCUPADOS Y BLOQUEADOS)
    useEffect(() => {
        if (!fecha || diaEstaBloqueado) {
            setHorariosDisponibles([]);
            setHora1('');
            setHora2('');
            setDosTurnos(false);
            return;
        }

        const ahora = new Date();
        const esHoy = fecha === fechaMin;
        const [year, month, day] = fecha.split('-').map(Number);
        const disponibles = [];
        
        const horasBloqueadasDelDia = horasBloqueadas[fecha] || [];

        for (let h = HORA_INICIO; h <= HORA_FIN; h++) {
            const horaStr = `${String(h).padStart(2, '0')}:00`;
            const slot = new Date(year, month - 1, day, h, 0, 0, 0).getTime();
            
            const ocupado = turnosOcupados.some(t => Math.abs(t - slot) < 3600000);
            const esPasado = esHoy && h <= ahora.getHours();
            const estaBloqueado = horasBloqueadasDelDia.includes(horaStr);

            if (!ocupado && !esPasado && !estaBloqueado) {
                disponibles.push(horaStr);
            }
        }
        setHorariosDisponibles(disponibles);
        setHora1('');
        setHora2('');
        setDosTurnos(false);
    }, [fecha, turnosOcupados, fechaMin, diaEstaBloqueado, horasBloqueadas]);

    const horariosSegundoTurno = () => {
        if (!hora1) return [];
        const [h] = hora1.split(':').map(Number);
        const siguiente = `${String(h + 1).padStart(2, '0')}:00`;
        return horariosDisponibles.includes(siguiente) ? [siguiente] : [];
    };

    const handleDosTurnosChange = (e) => {
        const checked = e.target.checked;
        setDosTurnos(checked);
        if (!checked) setHora2('');
    };

    const handleHora1Change = (e) => {
        const val = e.target.value;
        setHora1(val);
        if (dosTurnos) {
            const [h] = val.split(':').map(Number);
            const siguiente = `${String(h + 1).padStart(2, '0')}:00`;
            if (!horariosDisponibles.includes(siguiente)) {
                setHora2('');
                setError('No hay disponibilidad para el segundo turno consecutivo');
            } else {
                setHora2(siguiente);
                setError('');
            }
        }
    };

    const construirFechaISO = (fechaStr, horaStr) => {
        const [y, m, d] = fechaStr.split('-').map(Number);
        const [hh] = horaStr.split(':').map(Number);
        return new Date(y, m - 1, d, hh, 0, 0, 0);
    };

    // CREAR TURNOS Y ABRIR MODAL DE PAGO
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setExito('');

        if (!servicioSeleccionado) return setError('Seleccioná un tipo de masaje');
        if (!fecha) return setError('Seleccioná un día');
        if (!hora1) return setError('Seleccioná un horario');
        if (dosTurnos && !hora2) return setError('Seleccioná el segundo horario o desmarcá la opción');

        try {
            setProcesandoPago(true);
            const turnosCreados = [];
            const fecha1 = construirFechaISO(fecha, hora1);
            
            const res1 = await api.post('/appointments', {
                masajistaId: id,
                serviceId: servicioSeleccionado._id,
                fecha: fecha1.toISOString(),
                duracion: servicioSeleccionado.duracion,
                notas,
                estado: 'pendiente',
            });
            turnosCreados.push(res1.data._id);

            if (dosTurnos && hora2) {
                const fecha2 = construirFechaISO(fecha, hora2);
                const res2 = await api.post('/appointments', {
                    masajistaId: id,
                    serviceId: servicioSeleccionado._id,
                    fecha: fecha2.toISOString(),
                    duracion: servicioSeleccionado.duracion,
                    notas,
                    estado: 'pendiente',
                });
                turnosCreados.push(res2.data._id);
            }

            setIdsTurnosCreados(turnosCreados);
            setShowModalPago(true);
            setProcesandoPago(false);
            
        } catch (err) {
            setError(err.response?.data?.message || 'Error al reservar turno');
            setProcesandoPago(false);
        }
    };

    // PAGO POR MERCADO PAGO
    const pagarMercadoPago = async () => {
        try {
            setProcesandoPago(true);
            const response = await api.post('/payments/create-preference', {
                title: `Reserva de turno${idsTurnosCreados.length > 1 ? 's' : ''}`,
                description: `Seña para ${idsTurnosCreados.length} turno${idsTurnosCreados.length > 1 ? 's' : ''}`,
                appointmentIds: idsTurnosCreados,
            });
            window.location.href = response.data.init_point;
        } catch (err) {
            console.error('Error al iniciar MP:', err);
            setError('No se pudo iniciar el pago con Mercado Pago.');
            setProcesandoPago(false);
        }
    };

    // PAGO POR TRANSFERENCIA
    const confirmarTransferencia = async (e) => {
        e.preventDefault();
        if (!comprobante) {
            return setError('Por favor, subí una foto del comprobante de transferencia.');
        }

        try {
            setProcesandoPago(true);
            const formData = new FormData();
            formData.append('appointmentIds', idsTurnosCreados.join(','));
            formData.append('comprobante', comprobante);

            await api.post('/payments/transferencia', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setShowModalPago(false);
            setExito('Comprobante enviado. Tu turno será confirmado cuando validemos la transferencia.');
            setTimeout(() => navigate('/paciente/mis-turnos'), 3000);
        } catch (err) {
            console.error('Error al subir transferencia:', err);
            setError('Hubo un error al subir tu comprobante.');
            setProcesandoPago(false);
        }
    };

    const formatearPrecio = (valor) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(valor || 0);
    const totalSena = (dosTurnos && hora2 ? 2 : 1) * (servicioSeleccionado?.precioSena || 0);
    const tieneHorasBloqueadas = fecha && horasBloqueadas[fecha] && horasBloqueadas[fecha].length > 0;

    if (loading) return <div className="reservar-wrapper"><div className="reservar-container"><div className="reservar-box text-center">Cargando...</div></div></div>;

    if (!masajista) {
        return (
            <div className="reservar-wrapper">
                <div className="reservar-container">
                    <div className="reservar-box">
                        <div className="alert alert-warning">Masajista no encontrado</div>
                        <button className="btn btn-secondary" onClick={() => navigate('/paciente')}>Volver</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reservar-wrapper">
            <div className="reservar-container">
                <div className="reservar-box">
                    <div className="reservar-header">
                        <h2>Reservar turno con {masajista.nombre} {masajista.apellido}</h2>
                        <p>Elegí el tipo de masaje, el día y el horario</p>
                    </div>
                    {exito && <div className="alert alert-success">{exito}</div>}
                    {error && <div className="alert alert-danger">{error}</div>}

                    <form onSubmit={handleSubmit}>
                        {/* 1) Tipo de masaje */}
                        <div className="form-group">
                            <label>1. Elegí el tipo de masaje:</label>
                            {servicios.length === 0 ? (
                                <div className="alert alert-warning" style={{ marginBottom: 0 }}>No hay servicios disponibles</div>
                            ) : (
                                <div className="servicios-selector">
                                    {servicios.map(s => (
                                        <label key={s._id} className={`servicio-option ${servicioSeleccionado?._id === s._id ? 'selected' : ''}`}>
                                            <input type="radio" name="servicio" value={s._id} checked={servicioSeleccionado?._id === s._id} onChange={() => setServicioSeleccionado(s)} disabled={procesandoPago} />
                                            <div className="servicio-option-content">
                                                <div className="servicio-option-nombre">{s.nombre}</div>
                                                {s.descripcion && <div className="servicio-option-desc">{s.descripcion}</div>}
                                                <div className="servicio-option-meta">
                                                    <span>💰 {formatearPrecio(s.precio)}</span>
                                                    <span>📌 Seña: {formatearPrecio(s.precioSena)}</span>
                                                    <span>⏱️ {s.duracion} min</span>
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 2) Día */}
                        <div className="form-group">
                            <label>2. Elegí el día:</label>
                            <input
                                type="date"
                                className={`form-control ${diaEstaBloqueado ? 'is-invalid' : ''}`}
                                value={fecha}
                                min={fechaMin}
                                max={fechaMax}
                                onChange={handleFechaChange}
                                required
                                disabled={procesandoPago}
                            />
                            {diaEstaBloqueado && (
                                <div className="alert alert-danger" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '1.2rem' }}>🚫</span>
                                    <span>El profesional no está disponible este día (Vacaciones / Día libre). Por favor, elegí otra fecha.</span>
                                </div>
                            )}
                        </div>

                        {/* 3) Horario */}
                        {fecha && !diaEstaBloqueado && (
                            <div className="form-group">
                                <label>3. Elegí el horario:</label>
                                {tieneHorasBloqueadas && (
                                    <div className="alert alert-warning" style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                                        ⏳ El profesional tiene bloqueados estos horarios: <strong>{horasBloqueadas[fecha].join(', ')} hs</strong>.
                                    </div>
                                )}
                                {horariosDisponibles.length === 0 ? (
                                    <div className="alert alert-warning" style={{ marginBottom: 0 }}>No hay horarios disponibles para este día</div>
                                ) : (
                                    <select className="form-control" value={hora1} onChange={handleHora1Change} required disabled={procesandoPago}>
                                        <option value="">-- Seleccioná un horario --</option>
                                        {horariosDisponibles.map(h => (
                                            <option key={h} value={h}>{h} hs</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        {/* 4) Dos turnos */}
                        {hora1 && (
                            <div className="form-group dos-turnos-group">
                                {horariosSegundoTurno().length > 0 ? (
                                    <label className="checkbox-label">
                                        <input type="checkbox" checked={dosTurnos} onChange={handleDosTurnosChange} disabled={procesandoPago} />
                                        <span>¿Querés agendar dos turnos consecutivos? (El segundo sería a las {horariosSegundoTurno()[0]} hs)</span>
                                    </label>
                                ) : (
                                    <div className="alert alert-warning" style={{ marginBottom: 0 }}>No hay disponibilidad para un segundo turno consecutivo.</div>
                                )}
                            </div>
                        )}

                        {/* Notas */}
                        <div className="form-group">
                            <label>Notas (opcional):</label>
                            <textarea className="form-control" value={notas} onChange={(e) => setNotas(e.target.value)} rows="3" placeholder="Algún detalle para el masajista..." disabled={procesandoPago} />
                        </div>

                        {/* Resumen */}
                        {hora1 && servicioSeleccionado && (
                            <div className="resumen-turno">
                                <div className="resumen-detalle">
                                    <span>Seña ({dosTurnos && hora2 ? '2 turnos' : '1 turno'})</span>
                                    <strong>{formatearPrecio(totalSena)}</strong>
                                </div>
                                <small className="resumen-nota">El saldo restante se abona en el local</small>
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary w-100" disabled={procesandoPago || !hora1 || !servicioSeleccionado || diaEstaBloqueado}>
                            {procesandoPago ? 'Cargando...' : 'Continuar al pago'}
                        </button>
                    </form>

                    <div className="reservar-footer">
                        <button className="btn btn-secondary" onClick={() => navigate('/paciente')} disabled={procesandoPago}>
                            Volver al panel
                        </button>
                    </div>
                </div>
            </div>

            {/* MODAL DE PAGO */}
            {showModalPago && (
                <div className="modal-backdrop" onClick={() => setShowModalPago(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Elegí tu método de pago</h5>
                            <button className="btn-close" onClick={() => setShowModalPago(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {error && <div className="alert alert-danger">{error}</div>}
                            
                            {!metodoPago && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <p style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Seleccioná cómo querés abonar la seña:</p>
                                    <button className="btn btn-primary" onClick={() => setMetodoPago('mercadopago')}>
                                        💳 Pagar con Mercado Pago
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setMetodoPago('transferencia')}>
                                        🏦 Transferencia Bancaria
                                    </button>
                                </div>
                            )}

                            {metodoPago === 'mercadopago' && (
                                <div>
                                    <p style={{ marginBottom: '1rem', textAlign: 'center' }}>Serás redirigido a la plataforma de Mercado Pago para completar el pago.</p>
                                    <button className="btn btn-primary w-100" onClick={pagarMercadoPago} disabled={procesandoPago}>
                                        {procesandoPago ? 'Redirigiendo...' : 'Ir a Mercado Pago'}
                                    </button>
                                    <button className="btn btn-link w-100 mt-2" onClick={() => setMetodoPago('')} disabled={procesandoPago}>Volver</button>
                                </div>
                            )}

                            {metodoPago === 'transferencia' && (
                                <form onSubmit={confirmarTransferencia}>
                                    <div style={{ background: '#F5EDE3', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>
                                        <p style={{ margin: 0, fontWeight: 'bold' }}>Datos de la cuenta:</p>
                                        <p style={{ margin: '0.5rem 0', fontSize: '1.2rem', color: '#C67B5C' }}>Alias: <strong>tu.alias.aqui</strong></p>
                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>Titular: Tu Nombre / Tu Empresa</p>
                                    </div>
                                    <div className="form-group">
                                        <label>Subir comprobante (imagen):</label>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="form-control" 
                                            onChange={(e) => setComprobante(e.target.files[0])} 
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-success w-100" disabled={procesandoPago}>
                                        {procesandoPago ? 'Enviando...' : 'Confirmar Transferencia'}
                                    </button>
                                    <button type="button" className="btn btn-link w-100 mt-2" onClick={() => setMetodoPago('')} disabled={procesandoPago}>Volver</button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservarTurno;
